import { Cart, CartValidationResult } from '../types';
import { USE_MOCK_CART } from '../constants/buyer-portal';
import { cartMock } from '../mocks/cartMock';
import { clinicHttp } from './http';
import { ENDPOINTS } from './endpoints';
import {
  CartDtoBackend,
  CartValidationResultBackend,
  mapCartDto,
  mapCartValidation,
} from './cartMapper';

export type CartService = {
  get: () => Promise<Cart>;
  addItem: (productId: string, quantity: number) => Promise<Cart>;
  updateItem: (productId: string, quantity: number) => Promise<Cart>;
  removeItem: (productId: string) => Promise<Cart>;
  validate: () => Promise<CartValidationResult>;
  clear: () => Promise<void>;
};

const fetchCart = async (): Promise<Cart> => {
  const res = await clinicHttp.get<CartDtoBackend>(ENDPOINTS.BUYER.CART);
  return mapCartDto(res.data);
};

const realCartService: CartService = {
  get: fetchCart,
  addItem: async (productId, quantity) => {
    // Backend returns only the single CartItemDto; re-fetch full cart for totals.
    await clinicHttp.post(`${ENDPOINTS.BUYER.CART}/items`, { productId, quantity });
    return fetchCart();
  },
  updateItem: async (productId, quantity) => {
    await clinicHttp.put(ENDPOINTS.BUYER.CART_ITEM(productId), { quantity });
    return fetchCart();
  },
  removeItem: async (productId) => {
    await clinicHttp.delete(ENDPOINTS.BUYER.CART_ITEM(productId));
    return fetchCart();
  },
  validate: async () => {
    const [res, cart] = await Promise.all([
      clinicHttp.post<CartValidationResultBackend>(ENDPOINTS.BUYER.CART_VALIDATE),
      fetchCart(),
    ]);
    return mapCartValidation(res.data, cart);
  },
  clear: async () => {
    // Backend has no DELETE /buyer/cart. Loop-delete each item. Use allSettled
    // so one failed line (e.g. already removed) doesn't abort the rest and
    // leave the cart half-populated.
    const cart = await fetchCart();
    await Promise.allSettled(
      cart.items.map((i) =>
        clinicHttp.delete(ENDPOINTS.BUYER.CART_ITEM(i.productId)),
      ),
    );
  },
};

export const cartService: CartService = USE_MOCK_CART ? cartMock : realCartService;
